import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, alpha } from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import logger from '../utils/logger';

const accent = '#667eea';
const accentDark = '#764ba2';

const OktaLogin = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isDark } = useTheme();

  const login = async () => {
    setIsLoading(true);
    try {
      await onLogin();
    } catch (err) {
      logger.error('[OKTA-LOGIN] Login error:', err);
      setIsLoading(false);
    }
  };

  // Theme-aware colors
  const bgBase = isDark ? '#0f1019' : '#f5f5fa';
  const bgCard = isDark ? alpha('#181a27', 0.8) : alpha('#ffffff', 0.8);
  const cardBorder = isDark ? alpha(accent, 0.15) : alpha(accent, 0.12);
  const textPrimary = isDark ? '#e8eaf0' : '#1a1a2e';
  const textSecondary = isDark ? alpha('#e8eaf0', 0.6) : alpha('#1a1a2e', 0.55);
  const orbOpacity = isDark ? 0.08 : 0.06;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgBase,
        overflow: 'hidden',
      }}
    >
      {/* Floating ambient orbs */}
      {[
        { size: 400, top: '-10%', left: '-5%', color: accent, delay: '0s', duration: '20s' },
        { size: 350, bottom: '-15%', right: '-8%', color: accentDark, delay: '3s', duration: '25s' },
        { size: 250, top: '40%', right: '10%', color: accent, delay: '7s', duration: '22s' },
      ].map((orb, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: orb.size,
            height: orb.size,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(orb.color, orbOpacity)} 0%, transparent 70%)`,
            top: orb.top,
            bottom: orb.bottom,
            left: orb.left,
            right: orb.right,
            animation: `loginFloat${i} ${orb.duration} ease-in-out infinite`,
            animationDelay: orb.delay,
            pointerEvents: 'none',
            '@keyframes loginFloat0': {
              '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
              '33%': { transform: 'translate(30px, -20px) scale(1.05)' },
              '66%': { transform: 'translate(-20px, 15px) scale(0.95)' },
            },
            '@keyframes loginFloat1': {
              '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
              '33%': { transform: 'translate(-25px, 20px) scale(1.08)' },
              '66%': { transform: 'translate(15px, -25px) scale(0.92)' },
            },
            '@keyframes loginFloat2': {
              '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
              '50%': { transform: 'translate(-20px, -15px) scale(1.1)' },
            },
          }}
        />
      ))}

      {/* Login card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 440,
          width: '90%',
          p: { xs: 4, sm: 5 },
          borderRadius: '24px',
          background: bgCard,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${cardBorder}`,
          boxShadow: isDark
            ? `0 8px 32px ${alpha('#000', 0.3)}, 0 0 60px ${alpha(accent, 0.06)}`
            : `0 8px 32px ${alpha(accent, 0.08)}, 0 0 60px ${alpha(accent, 0.04)}`,
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src={isDark ? '/hciq-logo-white.svg' : '/hciq-logo-dark.svg'}
          alt="HealthCost IQ"
          sx={{
            height: 48,
            width: 'auto',
            maxWidth: '80%',
            objectFit: 'contain',
            mb: 3,
          }}
        />

        {/* Subtitle */}
        <Typography
          sx={{
            color: textSecondary,
            fontSize: '0.95rem',
            textAlign: 'center',
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          Sign in to your account to continue
        </Typography>

        {/* Sign In Button */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.5,
            }}
          >
            <CircularProgress
              size={22}
              sx={{
                color: accent,
              }}
            />
            <Typography sx={{ color: textSecondary, fontSize: '0.9rem' }}>
              Redirecting to Okta...
            </Typography>
          </Box>
        ) : (
          <Button
            onClick={login}
            startIcon={<LoginIcon />}
            sx={{
              width: '100%',
              py: 1.5,
              px: 3,
              borderRadius: '14px',
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              color: '#fff',
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
              boxShadow: `0 4px 16px ${alpha(accent, 0.35)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: `0 6px 24px ${alpha(accent, 0.5)}`,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            Sign in with Okta
          </Button>
        )}

        {/* Divider with "or" - for future auth methods */}
        {/* Could add more providers here later */}
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 24,
          color: textSecondary,
          fontSize: '0.75rem',
          textAlign: 'center',
        }}
      >
        {new Date().getFullYear()} HealthCost IQ Inc. All rights reserved.
      </Typography>
    </Box>
  );
};

export default OktaLogin;
